import {
    Autocomplete, Box,
    Button, Chip,
    Container,
    FormControl,
    InputLabel,
    MenuItem, OutlinedInput,
    Paper,
    Select,
    TextField
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Alert } from '@features/alert';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from '@shared/config/axios';
import { INITIAL_VALUE_SELECT } from '@shared/api/constants';
import { useTheme } from '@mui/material/styles';
import { AxiosErrorAlertHandler } from '@shared/api/utils';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

function getStyles(name, personName, theme) {
    return {
        fontWeight:
            personName.indexOf(name) === -1
                ? theme.typography.fontWeightRegular
                : theme.typography.fontWeightMedium,
    };
}


export const CreateStickerPage = () => {
    const theme = useTheme();
    const alertRef = useRef(null);

    const [qGrade, setQGrade] = useState('');
    const [weight, setWeight] = useState('');
    const [region, setRegion] = useState('');
    const [producerName, setProducerName] = useState('');
    const [altitude, setAltitude] = useState('');
    const [descriptors, setDescriptors] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedProcessing, setSelectedProcessing] = useState(null);
    const [selectedVariety, setSelectedVariety] = useState([]);
    const [selectedRoastType, setSelectedRoastType] = useState(INITIAL_VALUE_SELECT);

    // Options for select
    const [optionCountries, setOptionCountries] = useState([]);
    const [optionVarieties, setOptionVarieties] = useState([]);
    const [optionProcesses, setOptionProcesses] = useState([]);
    const [optionRoastTypes, setOptionRoastTypes] = useState([]);

    const { data: varieties } = useQuery({
        queryKey: ['varieties-for-sticker-pack'],
        queryFn: () => {
            return axios.get(`/varieties`).then((data) => data);
        }
    });

    const { data: countries } = useQuery({
        queryKey: ['countries-for-sticker-pack'],
        queryFn: () => {
            return axios.get(`/countries`).then((data) => data);
        }
    });

    const { data: processes } = useQuery({
        queryKey: ['processes-for-sticker-pack'],
        queryFn: () => {
            return axios.get(`/processes`).then((data) => data);
        }
    });

    const { data: roastTypes } = useQuery({
        queryKey: ['roastTypes-for-pack'],
        queryFn: () => {
            return axios.get(`/roast-types`).then((data) => data);
        }
    });

    useEffect(() => {
        if (varieties) {
            const newVarieties = varieties.data.map((item) => ( {
                label: item.name,
                value: item._id
            } ));
            setOptionVarieties(newVarieties);
        }
        if (countries) {
            const newCountries = countries.data.map((item) => ( {
                label: item.name,
                value: item._id
            } ));
            setOptionCountries(newCountries);
        }
        if (processes) {
            const newProcesses = processes.data.map((item) => ( {
                label: item.name?.ua,
                value: item._id
            } ));
            setOptionProcesses(newProcesses);
        }
        if (roastTypes) {
            const newRoastTypes = roastTypes.data.map((item) => ( {
                label: item.name.ua,
                value: item._id
            } ));
            setOptionRoastTypes(newRoastTypes);
        }
    }, [varieties, countries, processes, roastTypes]);

    const handleAltitudeChange = (event) => {
        const value = event.target.value;

        if (value === '' || /^\d+$/.test(value)) {
            const numericValue = value === '' ? '' : parseInt(value, 10);
            if (value === '' || ( numericValue > 0 && numericValue <= 8000 )) {
                setAltitude(value);
            }
        }
    };

    const handleProcessingChange = (event) => {
        const selectedValue = event.target.value;
        const selectedOption = optionProcesses.find(option => option.value === selectedValue);
        setSelectedProcessing(selectedOption);
    };

    const handleRoastTypeChange = (event) => {
        const selectedValue = event.target.value;
        const selectedOption = optionRoastTypes.find(option => option.value === selectedValue);
        setSelectedRoastType(selectedOption);
    };

    const handleWeightChange = (event) => {
        const value = event.target.value;
        if (/^\d*\.?\d*$/.test(value)) {
            setWeight(value);
        }
    };

    const handleSubmit = async () => {
        if (
            !weight ||
            !qGrade ||
            !region ||
            !producerName ||
            !altitude ||
            !descriptors ||
            !selectedCountry ||
            !selectedProcessing ||
            !selectedVariety ||
            !selectedRoastType
        ) {
            alertRef.current.handleOpen('warning', 'please fill out all fields!');
            return;
        }


        const newPack = {
            weight: weight,
            country: selectedCountry ? selectedCountry.label : {},
            processing: selectedProcessing ? selectedProcessing.label : {},
            roastType: selectedRoastType ? selectedRoastType.label : {},
            variety: selectedVariety ? selectedVariety : [],
            producerName: producerName,
            qGrade: qGrade,
            descriptors: descriptors,
            altitude: altitude,
            region: region,
        };

        try {
            const response = await axios.post('/qrcode/packs/create-packs-sticker', newPack, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');

            link.href = url;
            link.setAttribute('download', `${newPack.country}-${newPack.producerName}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            alertRef.current.handleOpen('success', 'packaging successfully packed');
        } catch (error) {
            AxiosErrorAlertHandler(error, alertRef);
        }
    };

    const clearStates = () => {
        setQGrade('');
        setWeight('');
        setRegion('');
        setProducerName('');
        setAltitude('');
        setDescriptors('');
        setSelectedCountry(null);
        setSelectedProcessing(null);
        setSelectedVariety([]);
        setSelectedRoastType(INITIAL_VALUE_SELECT);
    };

    return (
        <>
            <Container
                maxWidth="sm"
                sx={{
                    width: { xs: '100%', md: 'auto' },
                    padding: { xs: 1, sm: 2, md: 2 },
                    height: '80vh'
                }}
            >
                <Paper sx={{ p: 2, width: '100%', height: 'auto' }}>
                    <Grid container spacing={2}>
                        {/* Country */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    options={optionCountries}
                                    autoHighlight
                                    getOptionLabel={(option) => option.label}
                                    value={selectedCountry}
                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                    onChange={(event, newValue) => setSelectedCountry(newValue)}
                                    renderInput={(params) => <TextField {...params} label="country"/>}
                                    sx={{ width: '100%' }}
                                />
                            </FormControl>
                        </Grid>
                        {/* Producer Name */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <TextField
                                    label="producer name"
                                    variant="outlined"
                                    value={producerName}
                                    onChange={(e) => setProducerName(e.target.value)}
                                    fullWidth
                                />
                            </FormControl>
                        </Grid>
                        {/* Region */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <TextField
                                    label="region"
                                    variant="outlined"
                                    value={region}
                                    onChange={(e) => setRegion(e.target.value)}
                                    fullWidth
                                />
                            </FormControl>
                        </Grid>
                        {/* Q Grade*/}
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <FormControl fullWidth>
                                <TextField
                                    label="q grade"
                                    variant="outlined"
                                    value={qGrade}
                                    onChange={(e) => setQGrade(e.target.value)}
                                    fullWidth
                                />
                            </FormControl>
                        </Grid>
                        {/* Altitude */}
                        <Grid size={{ xs: 6, sm: 3 }}>
                            <FormControl fullWidth>
                                <TextField
                                    label="altitude"
                                    variant="outlined"
                                    value={altitude}
                                    onChange={handleAltitudeChange}
                                    fullWidth
                                />
                            </FormControl>
                        </Grid>
                        {/* Variety */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel id="demo-multiple-chip-label">variety</InputLabel>
                                <Select
                                    variant="outlined"
                                    labelId="demo-multiple-chip-label"
                                    id="demo-multiple-chip"
                                    multiple
                                    value={selectedVariety} // Теперь содержит массив с label
                                    onChange={(event) => {
                                        const selectedLabels = event.target.value; // MUI автоматически передаёт массив выбранных значений
                                        setSelectedVariety(selectedLabels);
                                    }}
                                    input={<OutlinedInput id="select-multiple-chip" label="variety"/>}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((label) => (
                                                <Chip key={label} label={label}/>
                                            ))}
                                        </Box>
                                    )}
                                    MenuProps={MenuProps}
                                >
                                    {optionVarieties.map((item) => (
                                        <MenuItem
                                            key={item.value}
                                            value={item.label} // Теперь value — это label
                                            style={getStyles(item, selectedVariety, theme)}
                                        >
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </Select>

                                {/*<Select*/}
                                {/*    variant="outlined"*/}
                                {/*    labelId="demo-multiple-chip-label"*/}
                                {/*    id="demo-multiple-chip"*/}
                                {/*    multiple*/}
                                {/*    value={selectedVariety}*/}
                                {/*    onChange={handleVarietyChange}*/}
                                {/*    input={<OutlinedInput id="select-multiple-chip" label="variety"/>}*/}
                                {/*    renderValue={(selected) => (*/}
                                {/*        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>*/}
                                {/*            {selected.map((selectedItem) => {*/}
                                {/*                const selectedOption = optionVarieties.find(option => option.value === selectedItem);*/}
                                {/*                return (*/}
                                {/*                    <Chip key={selectedItem} label={selectedOption?.label}/>*/}
                                {/*                );*/}
                                {/*            })}*/}
                                {/*        </Box>*/}
                                {/*    )}*/}
                                {/*    MenuProps={MenuProps}*/}
                                {/*>*/}
                                {/*    {*/}
                                {/*        optionVarieties.map((item) => (*/}
                                {/*            <MenuItem*/}
                                {/*                key={item.value}*/}
                                {/*                value={item.value}*/}
                                {/*                style={getStyles(item, selectedVariety, theme)}*/}
                                {/*            >*/}
                                {/*                {item.label}*/}
                                {/*            </MenuItem>*/}
                                {/*        ))*/}
                                {/*    }*/}
                                {/*</Select>*/}
                            </FormControl>
                        </Grid>
                        {/* Processing */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel id="select-processing-label">processing</InputLabel>
                                <Select
                                    variant="outlined"
                                    labelId="select-processing-label"
                                    label="processing"
                                    id="select-processing"
                                    value={selectedProcessing?.value || ''}
                                    onChange={handleProcessingChange}
                                >
                                    {optionProcesses.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {/* Roast Type */}
                        <Grid size={{ xs: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel id="select-roast-type-label">roast type</InputLabel>
                                <Select
                                    variant="outlined"
                                    labelId="select-roast-type-label"
                                    label="roast type"
                                    id="select-roast-type"
                                    value={selectedRoastType?.value || ''}
                                    onChange={handleRoastTypeChange}
                                >
                                    {optionRoastTypes.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {/* Weight */}
                        <Grid size={{ xs: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel id="select-weight-label">weight</InputLabel>
                                <Select
                                    variant="outlined"
                                    labelId="select-weight-label"
                                    label="weight"
                                    id="select-weight"
                                    value={weight}
                                    onChange={handleWeightChange}
                                >
                                    <MenuItem value={0.250}>250 г</MenuItem>
                                    <MenuItem value={0.500}>500 г</MenuItem>
                                    <MenuItem value={1}>1000 г</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {/* Descriptors */}
                        <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth>
                                <TextField
                                    label="descriptors"
                                    multiline
                                    value={descriptors}
                                    onChange={(e) => setDescriptors(e.target.value)}
                                    minRows={1}
                                    maxRows={10}
                                />
                            </FormControl>
                        </Grid>
                        {/* clear fields button */}
                        <Grid size={{ xs: 6, sm: 4 }}>
                            <Button
                                variant="contained"
                                color="inherit"
                                onClick={clearStates}
                                fullWidth
                            >
                                clear fields
                            </Button>
                        </Grid>
                        {/* create sticker button */}
                        <Grid size={{ xs: 6, sm: 8 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                fullWidth
                            >
                                create label
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
            <Alert ref={alertRef}/>
        </>
    );
};